import { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  VStack,
  Text,
  Input,
  Textarea,
  Select,
  Checkbox,
  IconButton,
  useToast,
  useColorModeValue as mode,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  Badge,
  Spinner,
  Stack
} from "@chakra-ui/react"
import { Tooltip } from "@chakra-ui/react"
import { AddIcon, EditIcon, DeleteIcon, InfoOutlineIcon } from "@chakra-ui/icons"
import { getTasks, createTask, updateTask, deleteTask } from "../redux/actions/taskActions"

const CATEGORIES = ["General", "Feature", "Bug", "Technical", "Other"]

const TasksScreen = () => {
  const dispatch = useDispatch()
  const toast = useToast()
  const { tasks, loading, error } = useSelector(state => state.task)
  const { isOpen, onOpen, onClose } = useDisclosure()
  
  const [isEditMode, setIsEditMode] = useState(false)
  const [currentTaskId, setCurrentTaskId] = useState(null)
  const [filterCategory, setFilterCategory] = useState("All")
  const [filterStatus, setFilterStatus] = useState("All")
  const nowLocal = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16)

  const [formData, setFormData] = useState({
    title: "",
    dateTime: nowLocal(),
    category: "General",
    description: "",
    assignedTo: "",
    done: false,
    doneDateTime: ""
  })

  useEffect(() => {
    dispatch(getTasks())
  }, [dispatch])

  useEffect(() => {
    if (error) {
      toast({
        title: "Error",
        description: error,
        status: "error",
        duration: 3000,
        isClosable: true
      })
    }
  }, [error, toast])

  const handleInputChange = e => {
    const { name, value, type, checked } = e.target
    setFormData(prev => {
      const next = { ...prev, [name]: type === "checkbox" ? checked : value }
      if (name === "done") {
        next.doneDateTime = checked ? nowLocal() : ""
      }
      return next
    })
  }

  const resetForm = () => {
    setFormData({
      title: "",
      dateTime: nowLocal(),
      category: "General",
      description: "",
      assignedTo: "",
      done: false,
      doneDateTime: ""
    })
    setIsEditMode(false)
    setCurrentTaskId(null)
  }

  const handleOpenAdd = () => {
    resetForm()
    onOpen()
  }

  const handleOpenEdit = task => {
    setIsEditMode(true)
    setCurrentTaskId(task._id)
    setFormData({
      title: task.title || "",
      dateTime: task.dateTime ? new Date(new Date(task.dateTime).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : nowLocal(),
      category: task.category || "General",
      description: task.description || "",
      assignedTo: task.assignedTo || "",
      done: task.done || false,
      doneDateTime: task.doneDateTime ? new Date(task.doneDateTime).toISOString().slice(0, 16) : ""
    })
    onOpen()
  }

  const handleSubmit = async () => {
    if (!formData.title || !formData.assignedTo || !formData.dateTime) {
      toast({
        title: "Validation Error",
        description: "Please fill in Title and Assigned To",
        status: "warning",
        duration: 3000,
        isClosable: true
      })
      return
    }

    try {
      if (isEditMode) {
        await dispatch(updateTask(currentTaskId, formData))
        toast({
          title: "Success",
          description: "Task updated successfully",
          status: "success",
          duration: 2000,
          isClosable: true
        })
      } else {
        await dispatch(createTask(formData))
        toast({
          title: "Success",
          description: "Task created successfully",
          status: "success",
          duration: 2000,
          isClosable: true
        })
      }
      onClose()
      resetForm()
    } catch (err) {
      // Error already handled by redux action
    }
  }

  const handleDelete = async id => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await dispatch(deleteTask(id))
        toast({
          title: "Success",
          description: "Task deleted successfully",
          status: "success",
          duration: 2000,
          isClosable: true
        })
      } catch (err) {
        // Error already handled by redux action
      }
    }
  }

  const formatDateTime = dateStr => {
    if (!dateStr) return ""
    const date = new Date(dateStr)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    })
  }

  const bgColor = mode("white", "gray.800")
  const borderColor = mode("gray.200", "gray.600")
  const hoverBg = mode("gray.50", "gray.700")
  const textColor = mode("gray.500", "gray.400")
  

  const filteredTasks = tasks.filter(t => {
    const catOk = filterCategory === "All" ? true : t.category === filterCategory
    const statusOk =
      filterStatus === "All" ? true : filterStatus === "Done" ? t.done === true : t.done === false
    return catOk && statusOk
  })

  const sortedTasks = [...filteredTasks].sort((a, b) => {
    if (a.done === b.done) return 0
    return a.done ? 1 : -1
  })

  return (
    <Box maxW="1200px" mx="auto" p={6}>
      <Flex justify="space-between" align="center" mb={4}>
        <Heading size="lg">Task Manager</Heading>
        <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={handleOpenAdd}>
          Add Task
        </Button>
      </Flex>

      <HStack spacing={3} mb={6}>
        <FormControl maxW="220px">
          <FormLabel mb={1}>Category</FormLabel>
          <Select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}>
            <option value="All">All</option>
            {CATEGORIES.map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </Select>
        </FormControl>
        <FormControl maxW="220px">
          <FormLabel mb={1}>Status</FormLabel>
          <Select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
            <option value="All">All</option>
            <option value="Pending">Pending</option>
            <option value="Done">Done</option>
          </Select>
        </FormControl>
      </HStack>

      {loading && tasks.length === 0 ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" />
        </Flex>
      ) : tasks.length === 0 ? (
        <Box textAlign="center" py={10}>
          <Text fontSize="lg" color="gray.500">
            No tasks found. Click "Add Task" to create one.
          </Text>
        </Box>
      ) : (
        <VStack spacing={3} align="stretch">
          {sortedTasks.map(task => (
            <Box
              key={task._id}
              p={4}
              bg={bgColor}
              borderWidth="1px"
              borderColor={borderColor}
              borderRadius="md"
              _hover={{ bg: hoverBg }}
              transition="all 0.2s"
            >
              <Flex justify="space-between" align="start">
                <Box flex="1">
                  <HStack mb={2} spacing={3}>
                    <Badge colorScheme={task.done ? "green" : "orange"}>
                      {task.done ? "Done" : "Pending"}
                    </Badge>
                    <Badge colorScheme="purple">{task.category}</Badge>
                    <Text fontSize="sm" color="gray.500">
                      {formatDateTime(task.dateTime)}
                    </Text>
                  </HStack>
                  <HStack mb={2} spacing={2} align="center">
                    {task.description && (
                      <Tooltip label={task.description} hasArrow placement="top-start">
                        <InfoOutlineIcon color="gray.500" />
                      </Tooltip>
                    )}
                    <Text fontWeight="bold" fontSize="lg" color={task.done ? textColor : "inherit"}>
                      {task.title}
                    </Text>
                  </HStack>
                  <Stack direction={{ base: "column", md: "row" }} spacing={4} fontSize="sm">
                    <HStack>
                      <Text fontWeight="semibold">Assigned to:</Text>
                      <Text>{task.assignedTo}</Text>
                    </HStack>
                    {task.done && task.doneDateTime && (
                      <HStack>
                        <Text fontWeight="semibold">Completed:</Text>
                        <Text>{formatDateTime(task.doneDateTime)}</Text>
                      </HStack>
                    )}
                  </Stack>
                </Box>
                <HStack spacing={2}>
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="yellow"
                    aria-label="Edit task"
                    onClick={() => handleOpenEdit(task)}
                  />
                  <IconButton
                    icon={<DeleteIcon />}
                    size="sm"
                    colorScheme="red"
                    aria-label="Delete task"
                    onClick={() => handleDelete(task._id)}
                  />
                </HStack>
              </Flex>
            </Box>
          ))}
        </VStack>
      )}

      {/* Add/Edit Task Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" closeOnOverlayClick={false}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{isEditMode ? "Edit Task" : "Add New Task"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Date & Time</FormLabel>
                <Input
                  type="datetime-local"
                  name="dateTime"
                  value={formData.dateTime}
                  isReadOnly
                  color={textColor}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Enter task title"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Category</FormLabel>
                <Select name="category" value={formData.category} onChange={handleInputChange}>
                  {CATEGORIES.map(cat => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Description (optional)</FormLabel>
                <Textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Enter task description"
                  rows={4}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Assigned To</FormLabel>
                <Input
                  name="assignedTo"
                  value={formData.assignedTo}
                  onChange={handleInputChange}
                  placeholder="Enter person name"
                />
              </FormControl>

              <FormControl>
                <Checkbox name="done" isChecked={formData.done} onChange={handleInputChange}>
                  Mark as Done
                </Checkbox>
              </FormControl>

              {formData.done && (
                <FormControl>
                  <FormLabel>Done Date & Time</FormLabel>
                  <Input
                    type="datetime-local"
                    name="doneDateTime"
                    value={formData.doneDateTime}
                    onChange={handleInputChange}
                  />
                </FormControl>
              )}
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {isEditMode ? "Update" : "Create"}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default TasksScreen
